import React, { useEffect, useState } from 'react';
import { Cookie, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type CookieConsentStatus = 'accepted' | 'declined' | 'customized' | null;

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
}

const GOOGLE_ANALYTICS_ID = 'G-3T551R7ECG';

const enableAnalytics = () => {
  if (window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = (command: string, ...args: unknown[]) => {
    window.dataLayer?.push([command, ...args]);
  };

  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ANALYTICS_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  script.dataset.auraAnalytics = 'true';
  document.head.appendChild(script);
};

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [cookieConsent, setCookieConsent] = useState<CookieConsentStatus>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent') as CookieConsentStatus;
    const savedSettings = localStorage.getItem('cookieSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings) as CookieSettings;
      setSettings(parsed);
      if (parsed.analytics) enableAnalytics();
    }
    setCookieConsent(consent);
  }, []);

  const save = (status: Exclude<CookieConsentStatus, null>, next: CookieSettings) => {
    setSettings(next);
    setCookieConsent(status);
    localStorage.setItem('cookieConsent', status);
    localStorage.setItem('cookieSettings', JSON.stringify(next));
    if (next.analytics) enableAnalytics();
  };

  const handleAcceptAll = () => save('accepted', {
    necessary: true,
    analytics: true,
  });

  const handleDeclineAll = () => save('declined', {
    necessary: true,
    analytics: false,
  });

  if (cookieConsent) return null;

  const optionalSettings = [
    ['analytics', t('cookie_consent.cookie_types.analytics.title'), t('cookie_consent.cookie_types.analytics.description')],
  ] as const;

  return (
    <aside
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-5xl border border-white/15 bg-[#171c27] text-white shadow-2xl"
      aria-label={t('cookie_consent.aria')}
    >
      <div className="p-5 sm:p-6">
        <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <div className="flex items-center gap-3">
              <Cookie className="h-5 w-5 text-[#c9a84c]" />
              <h3 className="font-serif text-xl">{t('cookie_consent.title')}</h3>
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
              {t('cookie_consent.description')}
            </p>
            <div className="mt-3 flex gap-4 text-xs text-white/50">
              <a href="/privacy" className="underline hover:text-white">{t('cookie_consent.privacy_policy')}</a>
              <a href="/cookie-policy" className="underline hover:text-white">{t('footer.legal.cookie_policy')}</a>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="inline-flex min-h-10 items-center gap-2 border border-white/20 px-4 text-xs font-semibold hover:bg-white/10"
            >
              <Settings size={14} />
              {showSettings ? t('cookie_consent.hide_settings') : t('cookie_consent.customize')}
            </button>
            {!showSettings && (
              <>
                <button
                  onClick={handleDeclineAll}
                  className="min-h-10 border border-white/20 px-4 text-xs font-semibold hover:bg-white/10"
                >
                  {t('cookie_consent.decline_all')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="min-h-10 bg-[#c9a84c] px-5 text-xs font-bold text-[#171817] hover:bg-[#d7bb68]"
                >
                  {t('cookie_consent.accept_all')}
                </button>
              </>
            )}
          </div>
        </div>

        {showSettings && (
          <div className="mt-5 grid gap-3 border-t border-white/15 pt-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="border border-white/10 p-3">
              <p className="text-sm font-semibold">{t('cookie_consent.cookie_types.necessary.title')}</p>
              <p className="mt-1 text-xs leading-5 text-white/50">{t('cookie_consent.cookie_types.necessary.description')}</p>
              <span className="mt-3 inline-block text-[10px] font-bold uppercase tracking-widest text-[#e8d9a0]">
                {t('cookie_consent.always_on')}
              </span>
            </div>
            {optionalSettings.map(([key, name, description]) => (
              <button
                key={key}
                onClick={() => setSettings((current) => ({ ...current, [key]: !current[key] }))}
                className="border border-white/10 p-3 text-left hover:border-white/25"
                aria-pressed={settings[key]}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold">{name}</span>
                  <span className={`h-4 w-4 rounded-full border ${
                    settings[key] ? 'border-[#c9a84c] bg-[#c9a84c]' : 'border-white/35'
                  }`} />
                </span>
                <span className="mt-1 block text-xs leading-5 text-white/50">{description}</span>
              </button>
            ))}
            <button
              onClick={() => save('customized', settings)}
              className="min-h-10 bg-[#c9a84c] px-5 text-xs font-bold text-[#171817] sm:col-span-2 lg:col-span-4"
            >
              {t('cookie_consent.save_preferences')}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
